<template>
    <main class="container-fluid pt-80 bg-gray-100 page-container px-0">
        <section class="searchRsl-conatiner d-flex container-xl px-0 pt-xl-3 pb-5">
            <template v-if="hydrated">
                <PanelsSideBar v-model:showSideBar="visible" :breakPoint="breakpoints.screen_laptop"
                    callFrom="fiterSideBar">
                    <PanelsFilterBar v-model:showFilterSideBar="visible" @scroll-to-top="handleScrollToTop"
                        @apply-search="handleApplySearch" />
                </PanelsSideBar>
            </template>
            <template v-else>
                <FilterBarSkeleton />
            </template>
            <div class="main-content w-100 search-nav">
                <!-- Desktop Header -->
                <div v-if="!hydrated">
                    <SortBarSkeleton />
                </div>
                <ClientOnly>
                    <div v-if="renderWidth >= breakpoints.screen_laptop" class="py-12 px-20 bg-white mb-4 rounded">
                        <div class="d-flex flex-wrap flex-sm-nowrap align-items-center justify-content-between">
                            <h4 class="fsz-18 fw-medium">
                                {{ displayCountText }}
                            </h4>
                            <div
                                class="d-flex flex-wrap flex-sm-nowrap gap-3 align-items-center justify-content-end col-6 ms-auto">
                                <label>Trier par: </label>
                                <div>
                                    <div class="select">
                                        <ClientOnly>
                                            <Dropdown inputId="desktop-sort-dropdown" @change="handleSortChange"
                                                v-model="triUserChoice" :options="triOrder" optionLabel="name"
                                                optionValue="code" placeholder="Pertinence"
                                                :pt="{ item: { class: 'p-0' } }">
                                                <template #option="slotProps">
                                                    <span class="text-gray-600 d-block py-2 p-3">
                                                        {{ slotProps.option.name }}
                                                    </span>
                                                </template>
                                            </Dropdown>
                                        </ClientOnly>
                                    </div>
                                </div>
                                <div class="d-flex">
                                    <span class="grid-row fsz-20" :class="[
                                        { active: gridView },
                                        gridView ? 'material-icons-round' : 'material-symbols-rounded'
                                    ]" @click="gridView = true">
                                        grid_view
                                    </span>
                                    <span class="grid-row" :class="{ active: !gridView }" @click="gridView = false">
                                        <i class="material-symbols-rounded fsz-20">menu</i>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Filter Tags -->
                        <div class="mt-3" v-if="searchStore.getFilterCounte">
                            <ul class="list-unstyled d-flex flex-wrap">
                                <li class="d-inline">
                                    <h4 class="fsz-18 fw-medium me-1">Filtres spécifiques:</h4>
                                </li>
                                <template v-for="(value, key) in searchStore.getUserInput" :key="key">
                                    <li class="d-inline" v-if="shouldDisplayFilter(key, value)">
                                        <div v-if="Array.isArray(value)">
                                            <span v-for="(val, index) in value" :key="index">
                                                <span v-if="shouldDisplayArrayItem(key, val)"
                                                    class="badge badge-solid-primary-600 d-inline-flex align-items-center me-1">
                                                    {{ getFilterDisplayText(key, val) }}
                                                    <i class="material-symbols-rounded fsz-14 cursor-pointer"
                                                        @click="clearSpecficFilter(searchStore.getUserInput, key, index)">
                                                        close
                                                    </i>
                                                </span>
                                            </span>
                                        </div>
                                        <div v-else-if="key === 'others' && value">
                                            <span
                                                class="badge badge-solid-primary-600 d-inline-flex align-items-center me-1">
                                                Autre
                                                <i class="material-symbols-rounded fsz-14 cursor-pointer"
                                                    @click="clearSpecficFilter(searchStore.getUserInput, key, 0)">
                                                    close
                                                </i>
                                            </span>
                                        </div>
                                        <div v-else-if="value && key != 'front'">
                                            <span
                                                class="badge badge-solid-primary-600 d-inline-flex align-items-center me-1">
                                                <span v-if="key === 'minPrice'">
                                                    {{ " de " + usePriceFormat(value) + " DT" }}
                                                </span>
                                                <span v-else-if="key === 'maxPrice'">
                                                    {{ " à " + usePriceFormat(value) + " DT" }}
                                                </span>
                                                <span v-else-if="key === 'fromRegYear'">
                                                    {{ " de l'année " + value }}
                                                </span>
                                                <span v-else-if="key === 'toRegYear'">
                                                    {{ " à l'année " + value }}
                                                </span>
                                                <span v-else-if="key === 'minKilometers'">
                                                    {{ " de " + usePriceFormat(value) + ' Km' }}
                                                </span>
                                                <span v-else-if="key === 'maxKilometers'">
                                                    {{ " à " + usePriceFormat(value) + ' Km' }}
                                                </span>
                                                <i class="material-symbols-rounded fsz-14 cursor-pointer"
                                                    @click="clearSpecficFilter(searchStore.getUserInput, key, 0)">
                                                    close
                                                </i>
                                            </span>
                                        </div>
                                    </li>
                                </template>
                            </ul>
                        </div>
                    </div>
                </ClientOnly>

                <MobileOptionsSheet
                    v-if="renderWidth < breakpoints.screen_mobile"
                    :visible="showTriSideBar"
                    :model-value="triUserChoice || null"
                    :options="triOrder"
                    optionLabel="name"
                    optionValue="code"
                    :multiple="false"
                    inputType="checkbox"
                    :auto-list-height="true"
                    :searchable="false"
                    emptyText="Aucun tri disponible"
                    :pending="searchStore.getPendingStats.fetchResult"
                    @update:visible="(value) => (showTriSideBar = value)"
                    @update:modelValue="applyMobileSort"
                />

                <!-- Mobile Navigation -->
                <ClientOnly>
                    <nav v-if="renderWidth < breakpoints.screen_laptop" class="mb-3 px-3"
                        :class="isMobileToolbarSticky ? 'sticky-toolbar bg-white py-14' : ''">
                        <div v-if="renderWidth < breakpoints.screen_laptop && !isMobileToolbarSticky"
                            class="d-flex pb-3 gap-2 align-items-center cursor-pointer">
                            <span class="d-flex" @click="navigateBackFromSearch"><i
                                    class="material-icons-outlined fsz-18">arrow_back</i></span>
                            <h4 class="fsz-16 fw-medium">
                                {{ displayCountText }}
                            </h4>
                        </div>
                        <div class="d-flex gap-2 align-items-center">
                            <div v-if="renderWidth < breakpoints.screen_laptop" class="filter-container" style="flex:1">
                                <button
                                    class="d-flex justify-content-center align-items-center btn small-btn btn-solid-primary-600 fsz-14"
                                    :class="renderWidth < breakpoints.screen_mobile && 'w-100'" @click="visible = true">
                                    <i class="material-icons-round fsz-18 me-1">tune</i>
                                    Filtres <span class="ms-1">({{ searchStore.getSelectedFilterCounte }})</span>
                                </button>
                            </div>
                            <div v-if="renderWidth < breakpoints.screen_mobile" class="filter-container"
                                style="flex: 1;">
                                <button
                                    class="d-flex justify-content-center align-items-center btn small-btn btn-solid-primary-600 fsz-14 w-100"
                                    @click="showTriSideBar = true">
                                    <i class="material-icons-round fsz-18 me-1">segment</i>
                                    Trier
                                </button>
                            </div>
                            <div v-if="renderWidth >= breakpoints.screen_mobile && renderWidth < breakpoints.screen_laptop"
                                class="d-flex flex-wrap flex-sm-nowrap gap-3 align-items-center justify-content-end col-6 ms-auto">
                                <label style="width: 80px;">Trier par: </label>
                                <div class="select">
                                    <ClientOnly>
                                        <Dropdown inputId="mobile-sort-dropdown" @change="handleSortChange"
                                            v-model="triUserChoice" :options="triOrder" optionLabel="name"
                                            optionValue="code" placeholder="Pertinence"
                                            :pt="{ item: { class: 'p-0' } }">
                                            <template #option="slotProps">
                                                <span class="text-gray-600 d-block py-2 p-3">
                                                    {{ slotProps.option.name }}
                                                </span>
                                            </template>
                                        </Dropdown>
                                    </ClientOnly>
                                </div>
                            </div>
                        </div>
                    </nav>
                </ClientOnly>

                <!-- Results Content -->
                <div v-if="hydrated && searchStore.getFetchResult?.announcements?.length === 0 && !searchStore.getPendingStats.fetchResult"
                    class="py-12 px-20 bg-white rounded-2 d-flex flex-column justify-content-center align-items-center"
                    style="height: 400px;">
                    <h5 class="pb-3">Aucun véhicule trouvé</h5>
                    <p class="text-center fsz-14">Nous n’avons trouvé aucun résultat pour votre recherche.</p>
                </div>

                <div v-if="isInitialLoading" class="px-3 px-xl-0 my-3">
                    <CardsSkeleton :count="skeletonCount" :view="skeletonView" />
                </div>

                <div class="px-3 px-xl-0" v-else>
                    <LazyCarCards v-if="searchStore.getFetchResult && searchStore.getFetchResult?.announcements"
                        @update-spinner="handleUpdateSpinner" :callFrom="'isSearch'" :data="mergedAnnouncements"
                        :className="'grid-cards-container-search'" :data-total-count="mergedTotalCount"
                        :method="loadMoreAnnouncements" :user-id="dashStore.getUserProfil.id"
                        :gridView="resolvedGridView" v-slot="slotProps">
                        <LazyCarCard :hasStatus="true" :key="slotProps.add.id" :announce="slotProps.add"
                            :ref="slotProps.loadMoreRef" :gridView="resolvedGridView"
                            :priority="slotProps.index === 0" />
                    </LazyCarCards>
                    <div v-if="isLoadingMore" class="d-flex justify-content-center align-items-center my-4">
                        <div class="spinner-border text-primary-600" role="status" style="width: 32px; height: 32px;">
                        </div>
                    </div>
                </div>

                <div class="d-flex mx-auto mt-3 save-search">
                    <button :disabled="searchStore.getPendingStats.saveSearch"
                        class="btn w-100 py-12 px-3 fsz-14 text-white saves-btn d-flex align-items-center justify-content-center gap-2"
                        @click="saveSearch">
                        <div v-if="searchStore.getPendingStats.saveSearch" class="spinner-border text-white"
                            role="status"></div>
                        <i v-else class="material-symbols-rounded fsz-20 text-white">notifications</i>
                        Sauver la recherche
                    </button>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useSearchStore } from "~/store/useSearchHome";
import { useToast } from "primevue/usetoast";
import { useScroll } from '~/composables/useScroll';
import { useWindowScroll, useThrottleFn } from "@vueuse/core";
import { breakpoints } from "~/utils/breakpoints";
import { usePromotedSearchResults } from "~/composables/usePromotedSearchResults";
import { getUniquePromotedAnnouncements, isPromotedAnnouncement } from "~/utils/promotedResults";
import type { announceById } from "~/utils/interface/announce";
import { initializeInputs } from "~/utils/searchFilters";
import { trackMetaCustomEvent } from "~/utils/meta/pixel";
import SortBarSkeleton from "~/components/Skeletons/SortBarSkeleton.vue";
import FilterBarSkeleton from "~/components/Skeletons/FilterBarSkeleton.vue";
import CardsSkeleton from "~/components/Skeletons/CardsSkeleton.vue";

const { scrollToTop } = useScroll();
const toast = useToast();
const dashStore = useDashboardStore();
const searchStore = useSearchStore();
const uiStore = useUiStateStore();
const { markReady, resetReady } = useScrollRestoration();
const router = useRouter();
const route = useRoute();
const runTimeConfig = useRuntimeConfig();
const { trackPageView, trackSearch } = useMetaPixel();
const BACK_SKIP_KEY = 'topauto:skip-search-refetch-on-back';
const FILTER_RELOAD_MARKER_KEY = 'topauto:filters:last-hard-reload-origin';

const { renderWidth, width, hydrated } = useDeviceWidth();

const { mergedAnnouncements, mergedTotalCount, refreshPromotedAnnouncements, promotedAnnouncements, hasActiveFilters } =
    usePromotedSearchResults(searchStore);

// Track loading states
const hasCachedResults = computed(
    () => hydrated.value && Boolean(searchStore.getFetchResult?.announcements?.length)
);
const showInitialSpinner = ref(!hasCachedResults.value);
const isLoadingMore = ref(false);
const isFirstLoad = ref(true);
const createEmptySearchResult = () => ({
    totalCount: 0,
    announcements: [] as announceById[],
    filters: {} as UserSearchInput,
});

const isHardReload = () => {
    if (!process.client) return false;

    const currentOrigin = String(performance.timeOrigin || Date.now());
    const markReloadAsHandled = () => {
        sessionStorage.setItem(FILTER_RELOAD_MARKER_KEY, currentOrigin);
    };
    const wasHandledForCurrentDocument = () =>
        sessionStorage.getItem(FILTER_RELOAD_MARKER_KEY) === currentOrigin;

    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navigationEntries.length) {
        if (navigationEntries[0].type !== 'reload') return false;
        if (wasHandledForCurrentDocument()) return false;
        markReloadAsHandled();
        return true;
    }

    const legacyNavigation = (performance as any)?.navigation;
    if (legacyNavigation?.type !== 1) return false;
    if (wasHandledForCurrentDocument()) return false;
    markReloadAsHandled();
    return true;
};

const resetFilterPageState = () => {
    searchStore.resetStoreValues();
    searchStore.searchResult = createEmptySearchResult();
    searchStore.setAnnounceCount(0);
    Object.assign(uiStore.search, {
        sortBy: '',
        filterSidebarOpen: false,
        sortSidebarOpen: false,
    });
    showInitialSpinner.value = true;
    isFirstLoad.value = true;

    if (process.client) {
        sessionStorage.removeItem(BACK_SKIP_KEY);
    }
};

const hydrateFiltersFromRouteQuery = async () => {
    if (!Object.keys(route.query || {}).length) {
        searchStore.userInput = {} as UserSearchInput;
        searchStore.selectedInput = {} as UserSearchInput;
        return;
    }

    const initializedInput = await initializeInputs(
        {} as UserSearchInput,
        searchStore,
        route.query
    );
    searchStore.userInput = { ...initializedInput };
    searchStore.selectedInput = { ...initializedInput };
};

const isInitialLoading = computed(() => {
    if (hasCachedResults.value) return false;
    if (!hydrated.value) return true;
    const noDataYet = !searchStore.getFetchResult?.announcements;
    return noDataYet || (showInitialSpinner.value && searchStore.getPendingStats.fetchResult);
});

// Throttle scroll events for better performance
const { y: scrollY } = useWindowScroll();

definePageMeta({
    layout: "client-layouts",
    keepScroll: true,
    scrollRestoration: true,
    middleware: 'redirect-invalid-filters'
});

const visible = computed({
    get: () => uiStore.search.filterSidebarOpen,
    set: (value) => {
        uiStore.search.filterSidebarOpen = value;
    }
});
const triUserChoice = computed({
    get: () => uiStore.search.sortBy,
    set: (value) => {
        uiStore.search.sortBy = value;
    }
});
const gridView = computed({
    get: () => uiStore.search.gridView ?? (renderWidth.value < breakpoints.screen_laptop),
    set: (value) => {
        uiStore.search.gridView = value;
    }
});
const showTriSideBar = computed({
    get: () => uiStore.search.sortSidebarOpen,
    set: (value) => {
        uiStore.search.sortSidebarOpen = value;
    }
});
const skeletonView = computed(() => {
    // Always check screen size first for responsive skeleton
    if (renderWidth.value < breakpoints.screen_laptop) {
        return 'grid';
    }
    // On desktop, use gridView preference if hydrated
    return hydrated.value && gridView.value ? 'grid' : 'row';
});
const skeletonCount = computed(() => {
    // Always check screen size first for responsive skeleton count
    if (renderWidth.value < breakpoints.screen_laptop) {
        return 6;
    }
    // On desktop, use gridView preference if hydrated
    return hydrated.value && gridView.value ? 8 : 4;
});
const resolvedGridView = computed(() =>
    hydrated.value ? gridView.value : renderWidth.value < breakpoints.screen_laptop
);

resetReady();

onMounted(() => {
    if (uiStore.search.gridView === null) {
        uiStore.search.gridView = width.value < breakpoints.screen_laptop;
    }
});
const triOrder = [
    { name: 'Les plus récents', code: 'MOST_RECENT' },
    { name: 'Les plus anciens', code: 'OLDEST' },
    { name: 'Prix croissant', code: 'PRICE_ASCENDING' },
    { name: 'Prix décroissant', code: 'PRICE_DESCENDING' },
    { name: 'Kilométrage croissant', code: 'KILOMETERS_ASCENDING' },
    { name: 'Kilométrage décroissant', code: 'KILOMETERS_DESCENDING' }
]

// Watch for filter changes to show spinner
watch(() => searchStore.getUserInput, () => {
    if (!isFirstLoad.value) {
        showInitialSpinner.value = true;
    }
}, { deep: true });

// Watch for sort changes to show spinner
watch(triUserChoice, () => {
    if (!isFirstLoad.value) {
        showInitialSpinner.value = true;
    }
});

// Watch for fetch completion to hide initial spinner
watch(() => searchStore.getPendingStats.fetchResult, (newVal) => {
    if (!newVal && showInitialSpinner.value) {
        showInitialSpinner.value = false;
        isFirstLoad.value = false;
    }
});

watch(
    () => searchStore.getPendingStats.fetchResult,
    async (pending) => {
        if (!pending) {
            await nextTick();
            markReady();
        }
    },
    { immediate: true }
);

const displayCountText = computed(() => {
    const count = searchStore.getFetchResult?.totalCount;
    if (typeof count === 'number') return count + ' véhicules en vente';
    return 'Aucun résultat';
});

const getCurrentSearchFilters = (): Record<string, unknown> => {
    const storeFilters = searchStore.filterUserSearchInput({ ...searchStore.getUserInput } as any) as Record<string, unknown>;
    if (Object.keys(storeFilters).length) return storeFilters;

    return Object.entries(route.query).reduce<Record<string, unknown>>((acc, [key, value]) => {
        if (typeof value === 'undefined' || value === null) return acc;
        acc[key] = Array.isArray(value) ? value.join('_') : value;
        return acc;
    }, {});
};

const getBoostedAnnouncementsForMeta = (): announceById[] => {
    const promotedFromStore = (searchStore.getPromotedSearchResult ?? []).filter((item) =>
        isPromotedAnnouncement(item)
    ) as announceById[];
    if (promotedFromStore.length) return promotedFromStore;

    const promotedFromMerged = mergedAnnouncements.value.filter((item) =>
        isPromotedAnnouncement(item)
    );
    if (promotedFromMerged.length) return promotedFromMerged;

    return (searchStore.getFetchResult?.announcements ?? []).filter((item) =>
        isPromotedAnnouncement(item)
    );
};

const trackSearchForBoostedAnnouncements = () => {
    if (runTimeConfig.public.clientBaseUrl !== 'https://topauto.tn') return;
    const boostedAnnouncements = getBoostedAnnouncementsForMeta();
    if (!boostedAnnouncements.length) return;

    trackSearch(
        getCurrentSearchFilters(),
        boostedAnnouncements.length,
        boostedAnnouncements
    );
};

const handleApplySearch = async () => {
    await fetchFirstPageWithPromotedFirst(undefined, { resetOldPromotedIds: true });
    trackSearchForBoostedAnnouncements();
};

const firstListingImage = computed(() => {
    const firstAnnounce = mergedAnnouncements.value[0];
    return firstAnnounce?.pictures?.[0]?.fileName || '';
});

useHead(() => firstListingImage.value ? {
    link: [
        {
            rel: 'preload',
            as: 'image',
            href: firstListingImage.value,
            fetchpriority: 'high'
        }
    ]
} : {});

const shouldDisplayFilter = (key: string, value: any): boolean => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (key === 'front') return false;
    return true;
};

const shouldDisplayArrayItem = (key: string, val: any): boolean => {
    if (!val) return false;
    if (key.includes('makesIds') || key.includes('modelsIds')) return false;
    return true;
};

const getFilterDisplayText = (key: string, val: any): string => {
    if (key === 'makes' || key === 'models') return val?.name || '';
    if (key === 'placesNbr') return 'Nombre de places: ' + val;
    return val;
};

const showToast = (severity: any, summary: string, detail: string) => {
    toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000
    });
};

// Handle spinner updates from CarCards
const handleUpdateSpinner = (spinnerState: boolean) => {
    isLoadingMore.value = spinnerState;
};

// Debounce the sort change handler
const handleSortChange = useThrottleFn(async () => {
    await fetchFirstPageWithPromotedFirst(triUserChoice.value);
}, 300);

const normalizeSortValue = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && typeof value.code === 'string') return value.code;
    return '';
};

const applyMobileSort = async (nextValue: any[] | any | null) => {
    const normalizedSort = normalizeSortValue(nextValue);
    triUserChoice.value = normalizedSort;
    await fetchFirstPageWithPromotedFirst(normalizedSort || undefined);
};

const clearSpecficFilter = async (
    obj: any,
    key: keyof UserSearchInput,
    index: number
) => {
    try {
        const query = { ...router.currentRoute.value.query };
        const nextInput: Record<string, any> = { ...obj };

        if (Array.isArray(nextInput[key])) {
            const nextArray = [...nextInput[key]];
            nextArray.splice(index, 1);

            if (nextArray.length) {
                nextInput[key] = nextArray;
            } else {
                delete nextInput[key];
            }

            // Update the query param if it exists
            if (query[key] && typeof query[key] === 'string') {
                const updatedValues = query[key].split('_').filter((_, i) => i !== index);
                if (updatedValues.length) {
                    query[key] = updatedValues.join('_');
                } else {
                    delete query[key];
                }
            }
        } else {
            delete nextInput[key];
            delete query[key]; // Remove the key from URL query
        }

        // Ensure store state is updated with a fresh object (reactivity + persistence)
        searchStore.userInput = { ...nextInput };
        searchStore.selectedInput = { ...nextInput };

        const sortBy = triUserChoice.value;
        await fetchFirstPageWithPromotedFirst(sortBy);

        // Update URL without reloading
        router.replace({ query });
    } catch (error) {
        // console.log(error);
    }
};

// Throttle the load more function
const loadMoreAnnouncements = useThrottleFn(async (page: number, userId: number, sort?: string) => {
    const sortToSend = sort ?? triUserChoice.value;
    await refreshPromotedAnnouncements();
    await searchStore.fetchAnnounce(page, userId, sortToSend);
}, 500);

const isMobileToolbarSticky = computed(
    () => width.value < breakpoints.screen_laptop && scrollY.value > 80
);

const hasLoadedAllResults = computed(() => {
    const total = searchStore.getFetchResult?.totalCount ?? 0;
    const loaded = searchStore.getFetchResult?.announcements?.length ?? 0;
    if (total === 0) return true;
    return loaded >= total;
});

const navigateBackFromSearch = async () => {
    closeMobilePanels();
    await navigateTo('/');
};

const closeMobilePanels = () => {
    visible.value = false;
    showTriSideBar.value = false;
};

const fetchFirstPageWithPromotedFirst = async (
    sortBy?: string,
    options?: { resetOldPromotedIds?: boolean }
) => {
    if (options?.resetOldPromotedIds) {
        searchStore.clearPromotedSearchResult();
    }

    await refreshPromotedAnnouncements({
        replace: true,
        resetOldPromotedIds: options?.resetOldPromotedIds,
    });
    await searchStore.fetchAnnounce(1, dashStore.getUserProfil.id, sortBy ?? triUserChoice.value);
    const count = searchStore.getFetchResult?.totalCount;
    if (typeof count === 'number') {
        searchStore.setAnnounceCount(count);
    }
};

const ensureResultsLoaded = async (options?: { force?: boolean }) => {
    if (!options?.force) {
        const announcements = searchStore.getFetchResult?.announcements;
        if (Array.isArray(announcements)) return;
    }

    await fetchFirstPageWithPromotedFirst();
};

const removeFooterHideClass = () => {
    if (!process.client) return;
    document.body.classList.remove('hide-footer');
};

const updateFooterVisibility = () => {
    if (!process.client) return;
    document.body.classList.toggle('hide-footer', !hasLoadedAllResults.value);
};

watch(hasLoadedAllResults, updateFooterVisibility, { immediate: true });

onBeforeRouteLeave(removeFooterHideClass);
onUnmounted(removeFooterHideClass);
onBeforeRouteLeave(() => {
    closeMobilePanels();
});
onDeactivated(() => {
    closeMobilePanels();
});
onActivated(async () => {
    closeMobilePanels();
    await ensureResultsLoaded();
});

watch(
    () => route.fullPath,
    async (nextPath, previousPath) => {
        // Skip when previousPath is undefined (initial page load)
        if (previousPath === undefined) return;

        closeMobilePanels();

        const nextHasQuery = Object.keys(route.query || {}).length > 0;
        const previousHadQuery = typeof previousPath === 'string' && previousPath.includes('?');
        if (!nextHasQuery && previousHadQuery) {
            searchStore.userInput = {} as UserSearchInput;
            searchStore.selectedInput = {} as UserSearchInput;
            await fetchFirstPageWithPromotedFirst(triUserChoice.value, {
                resetOldPromotedIds: true,
            });
        }

        if (runTimeConfig.public.clientBaseUrl !== 'https://topauto.tn' || nextPath === previousPath) return;
        trackPageView();
    }
);

const saveSearch = async () => {
    try {
        if (!dashStore.getIsUserConnected) {
            router.push('/authentication/login');
            return
        }
        await searchStore.saveSearchFilter();
        showToast(
            "success",
            "Recherche sauvegardée avec succès!",
            "Votre recherche a été sauvegardée. Vous recevrez des notifications pour les nouvelles correspondances."
        );
    } catch (error) {
        // console.log(error);
        showToast("error", "Error Message !", "generic");
    }
};

const handleScrollToTop = (scrollToTops: boolean) => {
    if (scrollToTops) {
        scrollToTop();
    }
};

const isAnnouncementDetailsPath = (value?: string) => /\/voitures\/[^/]+\/[^/]+\/\d+/.test(String(value || ''));
const isComingFromAnnouncementDetails = () => {
    if (!process.client) return false;
    const historyState = router.options.history.state || {};
    return isAnnouncementDetailsPath(historyState.back as string) || isAnnouncementDetailsPath(historyState.forward as string);
};

const consumeSkipRefetchFlag = () => {
    if (!process.client) return false;
    const shouldSkip = sessionStorage.getItem(BACK_SKIP_KEY) === '1';
    if (shouldSkip) {
        sessionStorage.removeItem(BACK_SKIP_KEY);
    }
    return shouldSkip;
};

const hydratePromotedFromCachedResults = () => {
    if (searchStore.getPromotedSearchResult?.length) return;

    const cachedAnnouncements = searchStore.getFetchResult?.announcements ?? [];
    const cachedPromoted = cachedAnnouncements.filter((item: any) =>
        item?.promotion?.isPromoted === true ||
        item?.isPromoted === true ||
        item?.promoted?.status === 'active'
    );
    const promotedPerBatch = hasActiveFilters.value ? 10 : 2;
    const maxNeeded = Math.ceil(cachedAnnouncements.length / 20) * promotedPerBatch;
    const hydratedPromoted = getUniquePromotedAnnouncements(cachedPromoted).slice(0, maxNeeded);
    if (hydratedPromoted.length) {
        promotedAnnouncements.value = hydratedPromoted;
    }
};

// Ensure results are fetched when landing on the filter page
onMounted(async () => {
    closeMobilePanels();
    const didHardReload = isHardReload();

    if (didHardReload) {
        resetFilterPageState();
        await hydrateFiltersFromRouteQuery();
    }

    const hasCachedAnnouncements = Boolean(searchStore.getFetchResult?.announcements?.length);
    const cameFromVoituresDetails = isComingFromAnnouncementDetails();
    const shouldSkipRefetch = hasCachedAnnouncements && (cameFromVoituresDetails || consumeSkipRefetchFlag());

    if (shouldSkipRefetch) {
        hydratePromotedFromCachedResults();
    } else {
        try {
            if (didHardReload) {
                await ensureResultsLoaded({ force: true });
            } else if (cameFromVoituresDetails) {
                if (!searchStore.getFetchResult?.announcements?.length) {
                    await fetchFirstPageWithPromotedFirst(triUserChoice.value);
                }
            } else {
                await ensureResultsLoaded();
            }
        } catch (e) {
            console.error(e);
        }
    }

    // Meta Pixel tracking for filter page
    if (runTimeConfig.public.clientBaseUrl === 'https://topauto.tn') {
        trackPageView();
        trackMetaCustomEvent('Page Filtres', {
            page_name: 'Filtres',
            page_type: 'search_filters',
            locale: 'fr-TN'
        });

        if (Object.keys(route.query).length) {
            trackSearchForBoostedAnnouncements();
        }
    }
});

// SEO and meta tags
const seoUrl = ref();
const seoImage = ref();
const seoUrlWebSite = ref();

const seoTitle = `Rechercher une voiture d’occasion en Tunisie | Trouvez le bon modèle - TopAuto`;
const seoDescription = `Cherchez parmi des milliers d’annonces de voitures d’occasion en Tunisie. Filtrez par marque, modèle, prix ou région et trouvez la voiture idéale sur TopAuto.`;
seoUrl.value = `${runTimeConfig.public.clientBaseUrl}${route.fullPath}`;
seoUrlWebSite.value = `${runTimeConfig.public.clientBaseUrl}`;
seoImage.value = `${runTimeConfig.public.clientBaseUrl}/_nuxt/assets/images/logos/Logo.png`;

useHead({
    title: seoTitle,
    link: [
        {
            rel: 'canonical',
            href: seoUrl
        }
    ],
    meta: [
        {
            name: 'description',
            content: seoDescription
        },
        ...(route.params.filter ? [{ name: 'robots', content: 'noindex, follow' }] : []),
        {
            property: 'og:title',
            content: seoTitle
        },
        {
            property: 'og:type',
            content: 'website'
        },
        {
            property: 'og:description',
            content: seoDescription
        },
        {
            property: 'og:url',
            content: seoUrl
        },
        {
            property: 'og:image',
            content: seoImage
        },
        {
            property: 'og:site_name',
            content: 'TopAuto'
        }
    ]
});

useSchemaOrg([
    defineWebSite({
        name: 'TopAuto',
        url: seoUrlWebSite,
        logo: seoImage,
    }),
    defineWebPage({
        name: seoTitle,
        description: seoDescription,
        url: seoUrl,
    }),
    defineOrganization({
        name: 'TopAuto',
        url: seoUrlWebSite,
        logo: seoImage,
    }),
    defineLocalBusiness({
        '@type': 'ProfessionalService',
        name: 'TopAuto',
        description: seoDescription,
        url: seoUrl,
        image: seoImage,
        areaServed: 'TN',
        availableLanguage: 'fr',
        serviceType: 'Recherche de voiture d’occasion',
    }),
])

// Clean up on unmount
onUnmounted(() => {
    showInitialSpinner.value = true;
    isFirstLoad.value = true;
});
</script>

<style lang="scss">
.select {
    min-width: 210px;
}

.custom-city .p-tooltip-text {
    font-size: 12px;
    background-color: #101828;
}

.p-tooltip {
    display: inline-block !important;
}

.searchRsl-conatiner {
    grid-column-gap: 30px;
}

.save-search {
    position: sticky;
    bottom: 20px;
    z-index: 10;
    width: 280px !important;
}

.saves-btn {
    background-color: #2c2e30;

    &:hover,
    &:active {
        background-color: #1a1b1d !important;
        border-color: #1a1b1d !important;
    }

    &:disabled {
        background-color: #000000 !important;
    }
}

.sticky-toolbar {
    position: sticky;
    top: 60px;
    z-index: 999;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

/* Safari-specific optimizations */
@media not all and (min-resolution:.001dpcm) {
    @supports (-webkit-appearance:none) {
        .grid-cards-container-search {
            -webkit-transform: translateZ(0);
            /* Promote to own layer */
            transform: translateZ(0);
        }

        .sticky-toolbar {
            position: -webkit-sticky;
        }

        /* Improve scrolling performance */
        .main-content {
            -webkit-overflow-scrolling: touch;
        }

        /* Reduce animation intensity for Safari */
        .spinner-border {
            -webkit-animation: spinner-border 0.75s linear infinite;
            animation: spinner-border 0.75s linear infinite;
        }

        /* Optimize filter badges */
        .badge {
            -webkit-font-smoothing: antialiased;
        }
    }
}

/* Reduce repaints on scroll */
.sticky-toolbar {
    will-change: transform;
}

/* Optimize dropdown rendering */
.p-dropdown-panel {
    transform: translateZ(0);
}

.list-spinner {
    min-height: 120px;
    margin-top: 12px;
    background: #FFF;
    border: 1px solid #f0f0f0;
}

.hide-footer footer {
    display: none !important;
}
</style>
